import { useEffect, useState, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { toast } from "sonner";

export const AlertsPopover = ({ onOpenDetail, refreshToken, onChanged, requireAdmin }) => {
  const [data, setData] = useState({ count: 0, items: [], threshold: 14 });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard/alerts", { params: { threshold: 14 } })
      .then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [refreshToken, load]);

  const markReceived = (id, e) => {
    e.stopPropagation();
    requireAdmin(() => doMark(id));
  };

  const doMark = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/purchases/${id}/receive`);
      toast.success("Item marked as received");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Please sign in to mark received" : "Failed to mark received");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="btn-alerts" className="relative h-9 w-9 rounded-lg flex items-center justify-center bg-slate-800/60 hover:bg-slate-700 border border-slate-700/70 transition">
          <Bell className="h-4 w-4 text-slate-200" />
          {data.count > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white flex items-center justify-center animate-urgent">
              {data.count > 99 ? "99+" : data.count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 bg-[#111D38] border-slate-700 text-slate-100 p-0" data-testid="alerts-popover">
        <div className="p-4 border-b border-slate-700/70 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <div>
            <div className="text-sm font-semibold">Aging Alerts</div>
            <div className="text-[11px] text-slate-400">WAITING SUPPLY {'>'} {data.threshold} days · {data.count} items</div>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-slate-800">
          {data.items.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">No alerts — great job!</div>
          )}
          {data.items.map((it) => (
            <div
              key={it.id}
              data-testid={`alert-item-${it.id}`}
              className="group p-3 hover:bg-amber-500/5 transition-colors"
            >
              <button onClick={() => onOpenDetail?.(it.id)} className="w-full text-left">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-mono text-amber-300 truncate">{it.po_number || it.pr_number || it.doc_number}</div>
                  <span className="flex items-center gap-1 text-[11px] text-rose-300 font-semibold flex-shrink-0">
                    <Clock className="h-3 w-3" /> {it.days_waiting} days
                  </span>
                </div>
                <div className="text-sm text-slate-200 mt-0.5 truncate">{it.description || "-"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                  {it.supplier || "Supplier not assigned"} · PR {fmtDate(it.pr_date)}
                  {it.eta_date && ` · ETA ${fmtDate(it.eta_date)}`}
                </div>
              </button>
              <button
                onClick={(e) => markReceived(it.id, e)}
                disabled={busyId === it.id}
                data-testid={`btn-receive-${it.id}`}
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3 w-3" />
                {busyId === it.id ? "Saving..." : "Mark Received"}
              </button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
